import { ScenarioCalculationRequest,
    ScenarioCalculationResponse,
    ScenarioCopyRequest,
    ScenarioCreate,
    ScenarioList,
    ScenarioResponse,
    ScenarioUpdate,
} from '../types/scenario';
import {
    CustomDeviationsApiResponse,
    CustomDeviationsReplaceRequest,
} from '../types/scenarioCustomDeviations';
import type {
  CustomMLModelResponse,
  CustomMLPreviewResponse,
  ScenarioCustomMLState,
} from '../types/customMl';
import type {
  AnalogueLayerConfig,
  AnalogueLayerState,
  AnalogueLooLayerResult,
  AnaloguePreviewLayerResult,
  MlMode,
  ScenarioModelState,
} from '../types/analogueMl';
import { resolveApiBaseUrl } from './apiBaseUrl';
import { apiClient } from './client';

export type ScenarioCurveType = 'ml' | 'p10' | 'p50' | 'p90';

export interface MlJobProgress {
  progress: number;
  message: string;
  stage: string;
}

function getAccessToken(): string {
  return (
    localStorage.getItem('access_token')
    || sessionStorage.getItem('access_token')
    || ''
  );
}

/** POST SSE job with CloudFront keep-alive heartbeats; resolves on final `result` event. */
async function streamMlJob<T>(
  path: string,
  body: unknown,
  onProgress?: (progress: MlJobProgress) => void,
): Promise<T> {
  const token = getAccessToken();
  const response = await fetch(`${resolveApiBaseUrl()}${path}`, {
    method: 'POST',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let detail = `HTTP error! status: ${response.status}`;
    try {
      const errBody = await response.json();
      if (errBody?.detail) {
        detail = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
      }
    } catch {
      // ignore
    }
    throw new Error(detail);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const data = JSON.parse(line.slice(6));
      if (data.type === 'error') {
        throw new Error(data.message || 'ML job failed');
      }
      if (data.type === 'result') {
        return data.data as T;
      }
      if (data.type === 'heartbeat' || data.progress !== undefined) {
        onProgress?.({
          progress: typeof data.progress === 'number' ? data.progress : 0,
          message: data.message || 'Working...',
          stage: data.stage || 'processing',
        });
      }
    }
  }

  throw new Error('Stream ended without result');
}

export interface ScenarioComparisonCurvePoint {
  year: number;
  oil: number;
  liquid: number;
  active_wells: number;
}

export interface ScenarioComparisonLayerSeries {
  scenario_layer_id: string;
  layer_name: string;
  recovery_factor?: number | null;
  initial_oil_rate: number;
  points: ScenarioComparisonCurvePoint[];
  base_points: Array<{
    year_index: number;
    oil: number;
    liquid: number;
  }>;
}

export interface ScenarioComparisonSeries {
  scenario_id: string;
  scenario_name: string;
  is_up_to_date: boolean;
  stale_reason?: string | null;
  points: ScenarioComparisonCurvePoint[];
  layers: ScenarioComparisonLayerSeries[];
}

export interface ScenarioComparisonResponse {
  curve_type: ScenarioCurveType;
  scenarios: ScenarioComparisonSeries[];
}

export const scenariosApi = {
  /**
   * Get list of scenarios with pagination and filtering
   */
  getScenarios: async (params?: {
    skip?: number;
    limit?: number;
    field_type?: string;
    calculation_status?: string;
  }): Promise<ScenarioList> => {
    const searchParams = new URLSearchParams();
    if (params?.skip !== undefined) searchParams.append('skip', params.skip.toString());
    if (params?.limit !== undefined) searchParams.append('limit', params.limit.toString());
    if (params?.field_type) searchParams.append('field_type', params.field_type);
    if (params?.calculation_status) searchParams.append('calculation_status', params.calculation_status);

    const queryString = searchParams.toString();
    const url = queryString ? `/scenarios/?${queryString}` : '/scenarios/';

    return apiClient.get<ScenarioList>(url);
  },

  /**
   * Get scenario by ID
   */
  getScenario: async (id: string): Promise<ScenarioResponse> => {
    return apiClient.get<ScenarioResponse>(`/scenarios/${id}`);
  },

  /**
   * Create new scenario
   */
  createScenario: async (data: ScenarioCreate): Promise<ScenarioResponse> => {
    return apiClient.post<ScenarioResponse>('/scenarios/', data);
  },

  /**
   * Update scenario
   */
  updateScenario: async (id: string, data: ScenarioUpdate): Promise<ScenarioResponse> => {
    return apiClient.put<ScenarioResponse>(`/scenarios/${id}`, data);
  },

  /**
   * Mark production/economics results stale after input edits
   */
  invalidateCalculations: async (id: string): Promise<ScenarioResponse> => {
    return apiClient.post<ScenarioResponse>(`/scenarios/${id}/invalidate-calculations`, {});
  },

  /**
   * Delete scenario
   */
  deleteScenario: async (id: string): Promise<void> => {
    return apiClient.delete(`/scenarios/${id}`);
  },

  /**
   * Copy scenario into the current user's organization (new private scenario).
   */
  copyScenario: async (id: string, data: ScenarioCopyRequest = {}): Promise<ScenarioResponse> => {
    return apiClient.post<ScenarioResponse>(`/scenarios/${id}/copy`, data);
  },

  /**
   * Calculate scenario
   */
  calculateScenario: async (id: string, data: ScenarioCalculationRequest): Promise<ScenarioCalculationResponse> => {
    return apiClient.post<ScenarioCalculationResponse>(`/scenarios/${id}/calculate`, data);
  },

  /**
   * Run calculations for scenario
   */
  runCalculations: async (id: string): Promise<{
    scenario_id: string;
    layers: Array<{
      layer_id: string;
      layer_name: string;
      predicted_q0: number;
      model_deviation: number;
      predicted_oil_base_curve: number[];
      max_predicted_oil_base_curve: number[];
      min_predicted_oil_base_curve: number[];
      P10: number[];
      P50: number[];
      P90: number[];
        predicted_oil_production: Array<{ date: string; oil: number }>;
        well_counts_by_year: Array<{ year: number; count: number }>;
    }>;
  }> => {
    return apiClient.post<{
      scenario_id: string;
      layers: Array<{
        layer_id: string;
        layer_name: string;
        predicted_q0: number;
        model_deviation: number;
        predicted_oil_base_curve: number[];
        max_predicted_oil_base_curve: number[];
        min_predicted_oil_base_curve: number[];
        P10: number[];
        P50: number[];
        P90: number[];
        predicted_oil_production: Array<{ date: string; oil: number }>;
        well_counts_by_year: Array<{ year: number; count: number }>;
      }>;
    }>(`/scenarios/${id}/calculations`);
  },

  /**
   * Get field types
   */
  getFieldTypes: async (): Promise<string[]> => {
    return apiClient.get<string[]>('/scenarios/field-types/list');
  },

  /**
   * Get calculation statuses
   */
  getCalculationStatuses: async (): Promise<string[]> => {
    return apiClient.get<string[]>('/scenarios/calculation-statuses/list');
  },

  /**
   * Get scenarios summary statistics
   */
  getScenariosSummary: async (): Promise<any> => {
    return apiClient.get<any>('/scenarios/stats/summary');
  },

  /**
   * Check if scenario calculations are up to date
   */
  getCalculationsStatus: async (id: string): Promise<{
    scenario_id: string;
    is_up_to_date: boolean;
    calculated_at: string | null;
    max_layer_updated_at: string | null;
  }> => {
    return apiClient.get<{
      scenario_id: string;
      is_up_to_date: boolean;
      calculated_at: string | null;
      max_layer_updated_at: string | null;
    }>(`/scenarios/${id}/calculations/status`);
  },

  /**
   * Get scenario calculations (saved results)
   */
  getScenarioCalculations: async (id: string): Promise<any> => {
    return apiClient.get<any>(`/scenarios/${id}/calculations`);
  },

  getCustomDeviations: async (id: string): Promise<CustomDeviationsApiResponse> => {
    return apiClient.get<CustomDeviationsApiResponse>(`/scenarios/${id}/custom-deviations`);
  },

  saveCustomDeviations: async (
    id: string,
    payload: CustomDeviationsReplaceRequest,
  ): Promise<CustomDeviationsApiResponse> => {
    return apiClient.post<CustomDeviationsApiResponse>(`/scenarios/${id}/custom-deviations`, payload);
  },

  clearCustomDeviations: async (id: string): Promise<CustomDeviationsApiResponse> => {
    return apiClient.post<CustomDeviationsApiResponse>(`/scenarios/${id}/custom-deviations`);
  },

  /**
   * Compare two scenarios on field-level yearly curves
   */
  compareFieldCurves: async (payload: {
    scenario_ids: string[];
    curve_type: ScenarioCurveType;
  }): Promise<ScenarioComparisonResponse> => {
    return apiClient.post<ScenarioComparisonResponse>('/scenarios/comparison/field-curves', payload);
  },

  getCustomMl: async (id: string): Promise<ScenarioCustomMLState> => {
    return apiClient.get<ScenarioCustomMLState>(`/scenarios/${id}/custom-ml`);
  },

  previewCustomMl: async (id: string, train_polygon: string): Promise<CustomMLPreviewResponse> => {
    return apiClient.post<CustomMLPreviewResponse>(`/scenarios/${id}/custom-ml/preview`, {
      train_polygon,
    });
  },

  trainCustomMl: async (
    id: string,
    payload: { name: string; train_polygon: string; existing_model_id?: string },
    onProgress?: (progress: MlJobProgress) => void,
  ): Promise<CustomMLModelResponse> => {
    return streamMlJob<CustomMLModelResponse>(
      `/scenarios/${id}/custom-ml/train/stream`,
      payload,
      onProgress,
    );
  },

  acceptCustomMl: async (id: string, model_id: string): Promise<CustomMLModelResponse> => {
    return apiClient.post<CustomMLModelResponse>(`/scenarios/${id}/custom-ml/accept`, { model_id });
  },

  getModelState: async (id: string): Promise<ScenarioModelState> => {
    return apiClient.get<ScenarioModelState>(`/scenarios/${id}/model`);
  },

  setModelMode: async (id: string, ml_mode: MlMode): Promise<ScenarioModelState> => {
    return apiClient.put<ScenarioModelState>(`/scenarios/${id}/model`, { ml_mode });
  },

  previewAnalogueMl: async (
    id: string,
    layers: AnalogueLayerConfig[],
  ): Promise<{ layers: AnaloguePreviewLayerResult[] }> => {
    return apiClient.post(`/scenarios/${id}/analogue-ml/preview`, { layers });
  },

  applyAnalogueMl: async (
    id: string,
    layers: AnalogueLayerConfig[],
    onProgress?: (progress: MlJobProgress) => void,
  ): Promise<{ layers: AnalogueLayerState[] }> => {
    return streamMlJob<{ layers: AnalogueLayerState[] }>(
      `/scenarios/${id}/analogue-ml/apply/stream`,
      { layers },
      onProgress,
    );
  },

  looTestAnalogueMl: async (
    id: string,
    layers: AnalogueLayerConfig[],
    onProgress?: (progress: MlJobProgress) => void,
  ): Promise<{ layers: AnalogueLooLayerResult[] }> => {
    return streamMlJob<{ layers: AnalogueLooLayerResult[] }>(
      `/scenarios/${id}/analogue-ml/loo-test/stream`,
      { layers },
      onProgress,
    );
  },

  setForecastEngine: async (
    id: string,
    forecast_engine: 'type_well' | 'material_balance',
  ): Promise<{ forecast_engine: string }> => {
    return apiClient.put(`/scenarios/${id}/forecast-engine`, { forecast_engine });
  },

  getMaterialBalance: async (
    scenarioId: string,
    layerId: string,
  ): Promise<import('../types/materialBalance').MaterialBalanceConfigResponse> => {
    return apiClient.get(`/scenarios/${scenarioId}/layers/${layerId}/material-balance`);
  },

  getMaterialBalanceMlStartDefaults: async (
    scenarioId: string,
  ): Promise<import('../types/materialBalance').MbMlStartDefaultsResponse> => {
    return apiClient.get(`/scenarios/${scenarioId}/material-balance/ml-start-defaults`);
  },

  suggestMaterialBalancePropsAi: async (
    scenarioId: string,
    layerId: string,
    body: import('../types/materialBalance').MbPropsAiRequest,
  ): Promise<import('../types/materialBalance').MbPropsAiResponse> => {
    return apiClient.post(
      `/scenarios/${scenarioId}/layers/${layerId}/material-balance/ai-props`,
      body,
    );
  },

  suggestMaterialBalanceWellsAi: async (
    scenarioId: string,
    layerId: string,
    body: import('../types/materialBalance').MbWellsAiRequest,
  ): Promise<import('../types/materialBalance').MbWellsAiResponse> => {
    return apiClient.post(
      `/scenarios/${scenarioId}/layers/${layerId}/material-balance/ai-wells`,
      body,
    );
  },

  putMaterialBalance: async (
    scenarioId: string,
    layerId: string,
    config: import('../types/materialBalance').MaterialBalanceConfig,
  ): Promise<{ ok: boolean; status: string }> => {
    return apiClient.put(`/scenarios/${scenarioId}/layers/${layerId}/material-balance`, { config });
  },

  simulateMaterialBalance: async (
    scenarioId: string,
    payload: {
      scenario_layer_id: string;
      inline?: Record<string, unknown>;
      persist?: boolean;
    },
  ): Promise<import('../types/materialBalance').MaterialBalanceResult> => {
    return apiClient.post(`/scenarios/${scenarioId}/material-balance/simulate`, payload);
  },

  optimizeMaterialBalance: async (
    scenarioId: string,
    payload: import('../types/materialBalance').MbOptimizeRequest,
  ): Promise<import('../types/materialBalance').MbOptimizeResponse> => {
    return apiClient.post(`/scenarios/${scenarioId}/material-balance/optimize`, payload);
  },

};
